"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { X, ArrowUpRight } from "lucide-react";
import { useIsEmbedded } from "../hooks/useIsEmbedded";
import { eigenerSub, merkeInstalliert } from "./install/merkzettel";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface PwaInstallBannerProps {
  /** App display name (e.g. "Contacts", "AI Chat"). */
  appName: string;
  /** Days to suppress after dismiss. Default: 30 (User-Spec) */
  dismissDays?: number;
  /** Logo-Bildpfad. Default `/logo.png`. Apps mit Host-Gesichtern (drive →
   *  docs/sheets/slides) reichen hier ihr per-Gesicht-Icon, sonst zeigt der
   *  Banner immer das Basis-Logo. */
  iconSrc?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DISMISS_KEY = "pwa-install-dismissed";

function isSuppressed(dismissDays: number): boolean {
  if (typeof window === "undefined") return true;
  // Nur nach aktivem Dismiss/Install (in den letzten N Tagen) unterdruecken —
  // bewusst wie das Cookie-Banner: zeigt bei jedem Load (auch nach Reload), bis
  // der User es aktiv schliesst. Re-Nag nach Dismiss verhindert der DISMISS_KEY.
  const wasDismissed = localStorage.getItem(DISMISS_KEY);
  if (wasDismissed) {
    const dismissedAt = parseInt(wasDismissed, 10);
    if (!isNaN(dismissedAt) && Date.now() - dismissedAt < dismissDays * 24 * 60 * 60 * 1000) {
      return true;
    }
  }
  return false;
}

function isIOS(): boolean {
  if (typeof window === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
}

// Manifests use different display modes (standalone/fullscreen) — all of them
// hide the browser chrome and count as "installed" for banner-suppression.
const INSTALLED_DISPLAY_MODES = [
  "standalone",
  "fullscreen",
  "minimal-ui",
  "window-controls-overlay",
];

function isStandalone(): boolean {
  if (typeof window === "undefined") return true;
  if (INSTALLED_DISPLAY_MODES.some((m) => window.matchMedia(`(display-mode: ${m})`).matches)) {
    return true;
  }
  return (window.navigator as unknown as { standalone?: boolean }).standalone === true;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PwaInstallBanner({
  appName,
  dismissDays = 30,
  iconSrc = "/logo.png",
}: PwaInstallBannerProps) {
  // Im iframe einer anderen Oekosystem-App gehoert die Huelle dem Embedder --
  // dieses Chrome-Element wuerde dort nur stoeren (siehe useIsEmbedded).
  const embedded = useIsEmbedded();
  // Auf der Install-Seite selbst waeren es zwei Install-Oberflaechen auf einem
  // Schirm — und die dort ist die bessere (sie erklaert auch den Weg von Hand).
  const pfad = usePathname();
  const aufInstallSeite = pfad === "/install" || pfad?.startsWith("/install/") === true;
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // 1. Schon installiert (PWA-standalone) → niemals zeigen.
    if (isStandalone()) {
      // …und das ist zugleich der einzige verlaessliche Moment, in dem die Suite
      // ueberhaupt erfaehrt, dass diese App auf diesem Geraet installiert ist:
      // `beforeinstallprompt` schweigt danach, und `getInstalledRelatedApps()`
      // meldet nur Eintraege aus `related_applications` (die kein Manifest der
      // Suite hat — dort stuende eine absolute Adresse, und die unterscheidet
      // sich zwischen Labs und Prod). Dieser Banner haengt in JEDER App-Huelle,
      // sieht also jeden Seitenaufruf INNERHALB der installierten App. Vorher
      // entstand der Merkzettel nur auf der Install-Seite — wer ueber die
      // Adressleiste installierte, kam dort nie wieder vorbei, und die Seite
      // erklaerte ihm beim naechsten Besuch die Installation, die er laengst
      // hinter sich hatte (gemeldet am 2026-09-15).
      merkeInstalliert(eigenerSub(window.location.hostname));
      return;
    }
    // 2. In dieser Session schon gezeigt ODER vor <dismissDays> Tagen
    //    dismissed → nicht zeigen.
    if (isSuppressed(dismissDays)) return;

    // Re-Check bei jedem `beforeinstallprompt`-Event: Browser kann das
    // Event mehrmals feuern (Engagement-Recompute, Tab-Wechsel). Ohne den
    // Re-Check zeigt jedes neue Event den Banner wieder, auch direkt nach
    // Dismiss → User-Pain "kommt jedes mal wieder beim Chat-Wechsel".
    const handler = (e: Event) => {
      e.preventDefault();
      if (isSuppressed(dismissDays)) return;
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (isIOS()) {
      // iOS hat kein beforeinstallprompt-Event — der Banner verweist dann auf /install
      const timer = setTimeout(() => {
        if (isSuppressed(dismissDays)) return;
        setShowIOSPrompt(true);
        setVisible(true);
      }, 2000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, [dismissDays]);

  if (embedded) return null;
  if (aufInstallSeite) return null;
  if (!visible) return null;
  if (!deferredPrompt && !showIOSPrompt) return null;

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      // Auch bei "accepted" den DISMISS_KEY setzen — der `isStandalone()`-
      // Check greift erst nach Re-Render/Reload, ohne Stamp koennte der
      // Banner zwischenzeitlich nochmal flashen.
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
      setVisible(false);
    } else {
      // User hat im OS-Prompt "Abbrechen" gedrueckt — wie Dismiss behandeln.
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setVisible(false);
  };

  return (
    // Position außen (statisches -translate-x), Animation auf innerem Wrapper.
    /* `bottom` rechnet den Cookie-Hinweis mit: der ist eine schwebende Karte auf
       `bottom-4` und liegt auf Ebene 110, also ueber diesem Banner. Auf dem Handy
       sind beide zentriert und gleich breit — ohne den Aufschlag stuende das
       Install-Angebot dahinter und sein Knopf waere nicht erreichbar (am
       2026-09-20 gemeldet). `--jw-cookieleiste-h` setzt `CookieConsent` selbst,
       solange der Hinweis steht, und meldet den belegten Streifen ab der
       Unterkante (Kartenhoehe + ihr Abstand); ohne Hinweis greift die 0 und es
       bleibt bei den 16 px. */
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[100] max-w-sm w-[calc(100%-2rem)]"
      style={{ bottom: "calc(1rem + var(--jw-cookieleiste-h, 0px))" }}
    >
      <div className="janeway-banner-in flex items-center gap-2 sm:gap-3 rounded-xl border border-border bg-card px-3 py-2 shadow-lg">
      <div className="flex items-center justify-center w-8 h-8 shrink-0">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={iconSrc} alt={appName} className="w-7 h-7 object-contain" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">Janeway {appName}</p>
        {deferredPrompt ? (
          <p className="text-xs text-muted-foreground mt-0.5">Als App installieren</p>
        ) : (
          /* Ohne Prompt (iOS, Firefox am Rechner, Safari) hilft kein Knopf, sondern
             nur der richtige Handgriff — und der haengt am Browser. Die
             Install-Seite kennt ihn, dieser Banner hat keinen Platz dafuer.
             Vorher stand hier fest die iOS-Anleitung; in jedem anderen
             Browser ohne Prompt blieb der Banner damit ohne Weg. */
          <a
            href="/install"
            className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1 underline decoration-dotted underline-offset-2 hover:text-foreground"
          >
            So installierst du sie
            <ArrowUpRight size={11} strokeWidth={2.25} aria-hidden />
          </a>
        )}
      </div>
      {deferredPrompt && (
        <button
          onClick={handleInstall}
          className="shrink-0 px-4 py-1.5 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          aria-label="App installieren"
        >
          Installieren
        </button>
      )}
      <button
        onClick={handleDismiss}
        className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        aria-label="Schließen"
      >
        <X size={14} />
      </button>
      </div>
    </div>
  );
}
